import { createContext, useContext } from "react";
import type { Season } from "../lib/queries/memberships";
import type { ID } from "../lib/id";

export interface SeasonContextValue {
  seasons: Season[];
  /** The single active season (where all writes happen), if configured. */
  activeSeason: Season | undefined;
  /** The season currently being viewed — defaults to the active one. */
  selectedSeason: Season | undefined;
  /** True when the viewed season is the active one (writes allowed). */
  isActiveSelected: boolean;
  /**
   * True when the viewed season is BEHIND the active one.
   *
   * Not the same question as `!isActiveSelected`, and the difference is
   * the whole point: a season the federation has opened for next year is
   * also "not active", and that one is legitimately writable — the
   * competition calendar is built months before its season goes active.
   * A season that is over is not: nothing may be created in it, and the
   * server refuses (competition.ErrSeasonInPast).
   *
   * False while the seasons are still loading, and false when either code
   * carries no year — the same forgiving answer `season.Ordinal` gives on
   * the server, so a naming convention cannot lock the calendar.
   */
  isSelectedPast: boolean;
  isLoading: boolean;
  /** Select a season to view; pass null to follow the active season. */
  setSelectedSeasonId: (id: ID | null) => void;
}

export const SeasonContext = createContext<SeasonContextValue | undefined>(
  undefined,
);

export function useSeason(): SeasonContextValue {
  const ctx = useContext(SeasonContext);
  if (!ctx) throw new Error("useSeason must be used within a SeasonProvider");
  return ctx;
}
